import { ref } from 'vue';
import expandUploadPath from './expandUploadPath';
import bugAlert from './bugAlert';
const firstImageLoaded = ref(false);

// only keep the first occurrence in the new array
function removeDuplicates(arr) {
  return arr.filter((value, index, self) => {
    return self.indexOf(value) === index;
  });
}

export default function usePreloadImages(
  content: Content,
  { justFirstScreen }: { justFirstScreen?: boolean } = {}
) {
  function preloadImages() {
    const largeImageWarnings: string[] = [];
    const firstScreen = content.pages[0].screens[0];

    // pages[0] without page.screens
    const firstPage = { ...content.pages[0] } as Partial<Page>;
    delete firstPage.screens;

    const mediaRegexWithQuery =
      /[" ][^" ]+\.(jpg|png|svg|mp4|json[^" ]*)(\?[^\s"]*)?\b/g;

    const firstScreenImages =
      (JSON.stringify(firstScreen) + ' ' + JSON.stringify(firstPage))
        .match(mediaRegexWithQuery)
        ?.map(s => s.slice(1)) || [];

    const uniqueFirstScreenImages = removeDuplicates(firstScreenImages);

    const allRemainingImages =
      JSON.stringify(content.pages)
        .match(mediaRegexWithQuery)
        ?.map(s => s.slice(1))
        .filter(s => !uniqueFirstScreenImages.includes(s)) || [];

    const uniqueRemainingImages = removeDuplicates(allRemainingImages);

    if (uniqueRemainingImages.length === 0) {
      firstImageLoaded.value = true;
      return;
    }

    const preloadImage = (src: string) =>
      new Promise(resolve => {
        try {
          const fullPreload = uniqueFirstScreenImages.includes(src);
          src = expandUploadPath(src);
          // console.log('preloadImage start', src, fullPreload && 'fullPreload');

          if (src.includes('.mp4')) {
            const video = document.createElement('video');
            video.addEventListener('canplaythrough', () =>
              setTimeout(() => {
                resolve(video);
              }, 1000)
            );
            video.onerror = () => resolve(false);
            if (fullPreload) video.setAttribute('preload', 'auto');
            if (!fullPreload) video.oncanplay = () => resolve(video);

            video.setAttribute('src', src);

            // do a full preload sometime in the first minutes
            setTimeout(
              () => video.setAttribute('preload', 'auto'),
              30e3 + 150e3 * Math.random()
            );
          } else {
            // preload image and warn if size is over 2MB
            fetch(src)
              .then(response => response.blob())
              .then(blob => {
                if (blob.size > 2e6) {
                  largeImageWarnings.push(
                    `(${(blob.size / 1e6).toFixed(1)}MB) ${src}`
                  );
                }
                resolve(src);
              })
              .catch(() => resolve(false));
          }
        } catch (e) {
          console.warn(e);
        }
      });

    if (!justFirstScreen) {
      const preloadRemainingPromises = uniqueRemainingImages.map((src, i) => {
        return new Promise(resolve => {
          setTimeout(() => preloadImage(src).then(resolve), 3000 + i * 250);
        });
      });

      // wait for all remaining images to be preloaded and then show the large images warning
      Promise.all(preloadRemainingPromises).then(() => {
        if (largeImageWarnings.length > 0) {
          bugAlert(
            `Large image warnings (${
              largeImageWarnings.length
            }):\n${largeImageWarnings.join('\n')}`
          );
        }
      });
    }

    return Promise.all(uniqueFirstScreenImages.map(preloadImage));
  }

  !(async () => {
    const timeout = new Promise(r => setTimeout(r, 7000));
    await Promise.race([preloadImages(), timeout]);

    firstImageLoaded.value = true;
  })();

  return firstImageLoaded;
}
